import { useLookbookProductItemContext } from '@app/components/products/context/LookbookProductItemContext';
import { useGalleryDots } from '@app/utils/hooks/useGalleryDots';
import { useIsTouchScreen } from '@app/utils/hooks/useIsTouchScreen';
import { Key } from '@app/utils/keys';
import React from 'react';
import { flushSync } from 'react-dom';

import { LookbookGalleryDot } from './LookbookGalleryDot';

const AVAILABLE_POSITIONS = 8;
const DOT_WIDTH = 24;
const DOT_WIDTH_ON_TOUCH = 32;
const DOTS_PADDING = 28;

interface Props {
  parentWidth: number;
}
export const LookbookGalleryDots = ({ parentWidth }: Props) => {
  const { activeSlideIndex, gallerySlides, setActiveSlideIndex } = useLookbookProductItemContext();

  const containerRef = React.useRef<HTMLDivElement>(null);
  const activeDotRef = React.useRef<HTMLButtonElement>(null);
  const totalCount = gallerySlides.length;
  const [visibleDotsCount, setVisibleDotsCount] = React.useState(-1);

  const isTouchScreen = useIsTouchScreen();

  const dotWidth = isTouchScreen ? DOT_WIDTH_ON_TOUCH : DOT_WIDTH;

  React.useEffect(() => {
    const scrollLeft = containerRef.current?.scrollLeft ?? 0;
    const shouldScrollToStart = !isTouchScreen && activeSlideIndex === 0 && scrollLeft > DOT_WIDTH;

    if (shouldScrollToStart) {
      containerRef.current?.scrollTo({ left: 0 });
    }
  }, [activeSlideIndex, isTouchScreen]);

  const { dotsStyles, wrapperRef } = useGalleryDots({
    activeIndex: activeSlideIndex,
    dotSize: dotWidth,
    onChange: setActiveSlideIndex,
    orientation: 'horizontal',
    totalCount,
    visibleCount: visibleDotsCount,
  });

  React.useEffect(() => {
    const availableSpace = parentWidth - DOTS_PADDING;
    const dotsCount = Math.floor(availableSpace / dotWidth);
    const validatedDotsCount = Math.min(dotsCount, AVAILABLE_POSITIONS);

    const shouldReserveSpaceForMoreLabel = validatedDotsCount < totalCount;
    const fittingDotsCount = shouldReserveSpaceForMoreLabel ? validatedDotsCount - 1 : validatedDotsCount;

    setVisibleDotsCount(fittingDotsCount);
  }, [parentWidth, dotWidth, totalCount]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== Key.LEFT && event.key !== Key.RIGHT) {
      return;
    }

    const index = event.key === Key.RIGHT ? Math.min(activeSlideIndex + 1, totalCount - 1) : Math.max(activeSlideIndex - 1, 0);

    flushSync(() => {
      setActiveSlideIndex(index);
    });

    activeDotRef.current?.focus();
  };

  const handleMouseOver = (index: number) => () => {
    setActiveSlideIndex(index);
  };

  const getDotSize = (index: number) => {
    if (!isTouchScreen) {
      return 'large';
    }

    const offset = Math.floor(visibleDotsCount / 2);
    const firstVisibleIndex = Math.min(hiddenDotsCount, Math.max(0, activeSlideIndex - offset));
    const lastVisibleIndex = firstVisibleIndex + visibleDotsCount - 1;

    const isFirstVisibleDot = index === firstVisibleIndex && index !== 0;
    const isLastVisibleDot = index === lastVisibleIndex && index !== totalCount - 1;

    return isFirstVisibleDot || isLastVisibleDot ? 'medium' : 'large';
  };

  const dotsContainerStyles = React.useMemo(() => {
    if (visibleDotsCount < 1) {
      return;
    }

    return {
      maxWidth: visibleDotsCount * dotWidth,
      overflow: isTouchScreen ? 'hidden' : 'auto',
    };
  }, [dotWidth, visibleDotsCount, isTouchScreen]);

  const shouldRenderDots = totalCount > 1;
  const hiddenDotsCount = totalCount - visibleDotsCount;

  if (!shouldRenderDots) {
    return null;
  }

  return (
    <div ref={wrapperRef} className="relative z-elevated flex h-6 w-full touch-none justify-center px-2">
      <div ref={containerRef} className="flex no-scrollbar" style={dotsContainerStyles}>
        <div className="-mx-1 flex items-center px-1" style={isTouchScreen ? dotsStyles : undefined}>
          {gallerySlides.map((slide, index) => (
            <LookbookGalleryDot
              key={slide.id}
              ref={activeDotRef}
              isActive={activeSlideIndex === index}
              isAvailable={slide.isAvailable}
              swatchData={slide.swatchData}
              size={getDotSize(index)}
              style={{ width: dotWidth }}
              onKeyDown={handleKeyDown}
              onMouseOver={handleMouseOver(index)}
              onFocus={handleMouseOver(index)}
            />
          ))}
        </div>
      </div>
      {hiddenDotsCount > 0 && <span className="self-center px-1 body-6">+{hiddenDotsCount}</span>}
    </div>
  );
};
